import { useEffect, useMemo, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import type { CartExtension } from "../../../core/ui/till/cart-extension";
import { formatCode } from "../domain/code";
import { stickersByLine, totalStickers, type ItemKind } from "../domain/earn";
import { cardState } from "../domain/balance";
import { knownCard, MODULE_ID, type KnownCard } from "../data/records";
import { kindResolver, isLoyaltyAnnotation, type LoyaltyAnnotation } from "../subscriber";
import { ScanCard, type AcceptedCard } from "./ScanCard";
import { loyaltyTheme, formatPesos } from "../../../theme/loyalty";

/**
 * The card on the current sale, in `till.cart.footer` (specs/006-loyalty US1, US2).
 *
 * The card itself rides on the cart as this module's annotation, so it is
 * cleared with the cart and can never pass to the next customer (research
 * R4). A reward is a line grant: core takes the item's price off and names
 * this module on the discount row (R3).
 *
 * NOTHING HERE CAN STOP A SALE. Every control is optional; "Take payment"
 * never waits on this panel (FR-015).
 */

export function CartCard({ cart }: SlotProps) {
  const extension = cart as CartExtension | undefined;
  if (!extension) return null;
  return <CartCardPanel cart={extension} />;
}

function CartCardPanel({ cart }: { cart: CartExtension }) {
  const raw = cart.annotation(MODULE_ID);
  const annotation = isLoyaltyAnnotation(raw) ? raw : null;
  const [scanning, setScanning] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [known, setKnown] = useState<KnownCard | null>(null);
  const [kinds, setKinds] = useState<Map<string, ItemKind>>(new Map());

  // How each item in the cart counts. Async because it reads the local
  // catalog; recomputed only when the set of items changes.
  const itemIds = useMemo(() => [...new Set(cart.lines.map((line) => line.menuItemId))].sort().join(","), [cart.lines]);
  useEffect(() => {
    let cancelled = false;
    const ids = itemIds === "" ? [] : itemIds.split(",");
    void kindResolver(ids).then((kindOf) => {
      if (!cancelled) setKinds(new Map(ids.map((id) => [id, kindOf(id)])));
    });
    return () => {
      cancelled = true;
    };
  }, [itemIds]);

  useEffect(() => {
    let cancelled = false;
    if (!annotation) {
      setKnown(null);
      return;
    }
    void knownCard(annotation.digest).then((card) => {
      if (!cancelled) setKnown(card);
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the digest alone: a new annotation object for the same card
    // (a reward redeemed) must not re-read the card mid-sale.
  }, [annotation?.digest]);

  const kindOf = (id: string): ItemKind => kinds.get(id) ?? "pastry";
  const earning = totalStickers(stickersByLine(cart.lines, kindOf));
  const ours = cart.lines.filter((line) => line.grantedByModule === MODULE_ID);

  const attach = (accepted: AcceptedCard) => {
    setScanning(false);
    const next: LoyaltyAnnotation = { cardCode: accepted.code, digest: accepted.digest, grants: [] };
    cart.annotate(MODULE_ID, next);
    setKnown(accepted.card);
  };

  const removeCard = () => {
    for (const line of ours) cart.revokeGrant(MODULE_ID, line.lineId);
    cart.annotate(MODULE_ID, null);
    setChoosing(false);
  };

  const redeem = (lineId: string, kind: "drink" | "pastry") => {
    if (!annotation) return;
    const granted = cart.grantLine(MODULE_ID, lineId, loyaltyTheme.rewardLineLabel);
    cart.annotate(MODULE_ID, { ...annotation, grants: [...annotation.grants, { lineId: granted, kind }] });
    setChoosing(false);
  };

  const undo = (lineId: string) => {
    if (!annotation) return;
    cart.revokeGrant(MODULE_ID, lineId);
    cart.annotate(MODULE_ID, { ...annotation, grants: annotation.grants.filter((grant) => grant.lineId !== lineId) });
  };

  if (!annotation) {
    return (
      <div className="loy__cart">
        <button type="button" className="loy__scan" onClick={() => setScanning(true)} disabled={cart.lines.length === 0}>
          {loyaltyTheme.till.scan}
        </button>
        {scanning && <ScanCard onAccept={attach} onClose={() => setScanning(false)} />}
      </div>
    );
  }

  // What the card will read once this sale lands: rewards it already had,
  // less those used here, and its stickers plus what this sale earns.
  const state = known ? cardState(known.L, known.R) : null;
  const usable = state ? state.banked - ours.length : 0;
  const after = known ? cardState(known.L + earning, known.R + ours.length) : null;

  const eligible = cart.lines.filter((line) => {
    if (line.grantedByModule !== null) return false;
    const kind = kindOf(line.menuItemId);
    return kind === "drink" || kind === "pastry";
  });

  return (
    <div className="loy__cart">
      <div className="loy__card-row">
        <span className="loy__code">{formatCode(annotation.cardCode)}</span>
        <button type="button" className="loy__link" onClick={removeCard}>
          Remove card
        </button>
      </div>

      {state && (
        <p className="loy__stickers">
          {state.onCard} of {state.every}
          {earning > 0 && <strong> · +{earning} this sale</strong>}
          {after && after.banked > state.banked - ours.length && <span> · completes a card</span>}
        </p>
      )}

      {ours.map((line) => (
        <div key={line.lineId} className="loy__granted">
          <span>
            Free: {line.itemName}
            {line.variantName ? ` ${line.variantName}` : ""} ({formatPesos(line.unitPrice)})
          </span>
          <button type="button" className="loy__link" onClick={() => undo(line.lineId)}>
            Undo
          </button>
        </div>
      ))}

      {usable > 0 && (
        <button type="button" className="loy__reward" onClick={() => setChoosing((open) => !open)}>
          {loyaltyTheme.till.rewardReady}
          {usable > 1 ? ` · ${usable}` : ""} — redeem
        </button>
      )}

      {choosing && usable > 0 && (
        <div className="loy__choose" role="group" aria-label="Choose the free item">
          {eligible.length === 0 ? (
            <p className="loy__note">Add the drink or pastry they want, then redeem.</p>
          ) : (
            eligible.map((line) => {
              const kind = kindOf(line.menuItemId) as "drink" | "pastry";
              return (
                <button key={line.lineId} type="button" className="loy__option" onClick={() => redeem(line.lineId, kind)}>
                  {line.itemName}
                  {line.variantName ? ` ${line.variantName}` : ""}
                  <span className="money"> {formatPesos(line.unitPrice)}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
